import { POOL_REGISTER_GAP_MS } from '../types';
import { enabledServices } from '../platforms';
import { expectsEcho } from '../delivery';
/**
 * 站方 systemMessage 的實際形狀：`ek21.ts` 對「非字串」payload 直接 `stringify`，
 * 所以 `{message: "..."}` 會原樣變成字串送進來；圖是 `img:<32 位 hash>` 文字標記。
 * 這裡放一份「跟線上一致」的樣本，讓 mock/截圖能看到修好後的樣子（不要用假字串）。
 */
const SAMPLE_IMAGE_HASH = '578a8d8a2f8d5b3c1e9f7a6b5c4d3e2f';
const WELCOME_JSON = JSON.stringify({
    message: `熱烈歡迎 img:${SAMPLE_IMAGE_HASH} 小站長 海洋之星 駕到！`,
});
/**
 * 每個帳號「最後一則」的樣本：正好覆蓋顯示層要處理的四種情況
 * （JSON 歡迎詞、尾端落單引號、正常中文引號、JSON 公告）。
 */
const TAIL_SAMPLES = [
    { type: 'join', from: '小花', text: WELCOME_JSON },
    { type: 'say', from: '阿宏', text: '剛進來"' },
    { type: 'say', from: 'Lily', text: '他說「你好」，人很好' },
    {
        type: 'join',
        from: '飲料店',
        text: JSON.stringify({ message: '歡迎來到 UT 大廳，請先看板規' }),
    },
    undefined,
];
/**
 * mock 的「帳號模式」名稱：前兩顆當帳號登入（名字取自假帳號池），其餘訪客。
 * 目的只是讓 `/api/sessions` 的歸因欄位不是整排空的（demo 驗收才不會誤判後端壞掉）。
 */
const MOCK_ACCOUNT_NAMES = ['rea7k2m9x4f', 'rea7k2m9ab1', null, null, null];
const PEERS = {
    ek21: ['小夜', '阿宏', 'Lily', '路人甲', '台中小雨', 'kevin'],
    ut: ['台北阿哲', 'momo', '夜貓', '匿名訪客', '小綠'],
};
const LINES = [
    { nickname: '', text: '大家安安' },
    { nickname: '', text: '有人在嗎？' },
    { nickname: '', text: '今天好熱' },
    { nickname: '', text: '這間房人好少' },
    { nickname: '', text: '剛下班，來晃晃' },
    { nickname: '', text: '哈哈哈 也太好笑' },
    { nickname: '', text: '有人住台中嗎' },
    { nickname: '', text: '先潛水一下' },
];
const ROOMS = [
    { id: 'vi_25', name: 'vi_25（低人流）', online: 7, service: 'ek21' },
    { id: 'vi_01', name: 'vi_01', online: 23, service: 'ek21' },
    { id: 'vi_08', name: 'vi_08', online: 4, service: 'ek21' },
    { id: 'ut_1', name: 'UT 大廳 1', online: 41, service: 'ut' },
    { id: 'ut_9', name: 'UT 測試房', online: 0, service: 'ut' },
];
export function createMockApiClient(options = {}) {
    const seed = options.accounts ?? [
        { service: 'ek21', nickname: '小安', room: 'vi_25' },
        { service: 'ek21', nickname: '阿凱', room: 'vi_25' },
        { service: 'ek21', nickname: 'Nina', room: 'vi_01' },
        { service: 'ut', nickname: '訪客一號', room: 'ut_1' },
        { service: 'ut', nickname: '訪客二號', room: 'ut_1' },
    ];
    const accounts = seed.map((item, index) => ({
        id: `mock-${index + 1}`,
        service: item.service,
        nickname: item.nickname,
        room: item.room,
        online: true,
        createdAt: Date.now() - index * 60_000,
        eventCount: 0,
    }));
    const handlers = new Set();
    const timers = new Set();
    let seq = 0;
    /**
     * 假帳號池（`/api/accounts`）：兩顆 ek21。mock 也照後端規則節流（一次 ≤3、兩批 ≥20 秒），
     * 也能驗「沒有 hasToken 時畫面顯示 —」與「有 hasToken 時顯示真值」兩種情況。
     * 欄位形狀照 `PoolAccount`（真的 `/api/accounts` 回什麼，這裡就回什麼）。
     */
    const pool = [
        {
            service: 'ek21',
            username: 'ek21-demo-a',
            createdAt: Date.now() - 3_600_000,
            lastLoginAt: Date.now() - 600_000,
            ok: true,
            hasToken: true,
            tokenValid: true,
            lastUsedAt: Date.now() - 600_000,
            usageCount: 7,
            rebuilds: 3,
            relogins: 0,
            source: 'console',
            held: true,
            heldBySession: 'mock-session-a',
        },
        { service: 'ek21', username: 'rea7k2m9ab1', createdAt: Date.now() - 600_000, lastLoginAt: null, ok: false, hasToken: false, source: 'manual', held: false },
    ];
    let poolSeq = 0;
    let lastRegisterAt = 0;
    /* ── Admin（`/api/admin/*`）的假資料 ─────────────────────────────────────
     * 形狀照 docs/DESIGN-admin.md §4.3；數字盡量從上面的 accounts／pool 算出來，
     * 這樣 demo 站的「總覽」跟「帳號池」互相對得上（不用各自編一套）。
     */
    /** 池子依服務計數。 */
    const countsByService = () => {
        const counts = { ek21: 0, ut: 0 };
        // 計數型別允許 null（代表「沒量到」）；mock 一律量得到，所以遇到 null 視為 0。
        for (const item of pool)
            counts[item.service] = (counts[item.service] ?? 0) + 1;
        return counts;
    };
    /** 我方 session 依房間聚合（同一間房多個 session 就是一列）。 */
    const roomUsage = () => {
        const rows = new Map();
        for (const account of accounts) {
            if (!account.room)
                continue;
            const key = `${account.service}:${account.room}`;
            const row = rows.get(key) ?? { service: account.service, room: account.room, mine: 0, roomOnline: 0 };
            row.mine += 1;
            rows.set(key, row);
        }
        for (const item of rows.values()) {
            const room = ROOMS.find((entry) => entry.id === item.room && entry.service === item.service);
            item.roomOnline = room?.online ?? item.mine;
        }
        return [...rows.values()];
    };
    /** 近 7 天（含今天，由舊到新）；第 3 天故意留 `null` = 那天沒跑。 */
    const registrationDays = () => {
        const days = [];
        for (let back = 6; back >= 0; back -= 1) {
            const date = new Date(Date.now() - back * 86_400_000).toISOString().slice(0, 10);
            const ran = back !== 3;
            days.push({
                date,
                ek21: ran ? (back % 3 === 0 ? 3 : 2) : null,
                ut: ran ? (back % 2 === 0 ? 2 : 1) : null,
                failed: ran ? (back === 1 ? 3 : 0) : null,
            });
        }
        return days;
    };
    /** 四筆批次紀錄（新→舊）：最後一筆故意「全滅」，讓 A1 的告警與 A4 的表格有東西看。 */
    const runs = [
        {
            id: 'run_mock_4',
            at: Date.now() - 42 * 60_000,
            source: 'grow_pool',
            service: 'ut',
            requested: 3,
            created: 0,
            failed: 3,
            durationMs: 4821,
            hitSite: true,
            errors: ['HTTP 429 登入嘗試次數過多（mock）'],
            poolAfter: { ek21: 2, ut: 0 },
        },
        {
            id: 'run_mock_3',
            at: Date.now() - 26 * 3_600_000,
            source: 'grow_pool',
            service: 'ek21',
            requested: 3,
            created: 2,
            failed: 1,
            durationMs: 6210,
            hitSite: true,
            errors: ['第 2 顆登入失敗（mock）'],
            poolAfter: { ek21: 2, ut: 0 },
        },
        {
            id: 'run_mock_2',
            at: Date.now() - 50 * 3_600_000,
            source: 'manual',
            service: 'ut',
            requested: 1,
            created: 1,
            failed: 0,
            durationMs: 2410,
            hitSite: true,
            errors: [],
            poolAfter: { ek21: 1, ut: 0 },
        },
        {
            id: 'run_mock_1',
            at: Date.now() - 74 * 3_600_000,
            source: 'console',
            service: 'ek21',
            requested: 2,
            created: 1,
            failed: 1,
            durationMs: 5300,
            hitSite: true,
            errors: ['站方回 500（mock）'],
            poolAfter: { ek21: 1, ut: 0 },
        },
    ];
    /**
     * 每個帳號的事件歷史（= `GET /api/sessions/:id/events` 的 mock）。
     *
     * 為什麼一定要有：`useInbox` 打開對話時會先拉歷史，`client` 沒有 `listEvents`
     * 就直接丟錯 → demo 站（純 mock）開房會出現紅字提示條、對話串永遠空的。
     * 行為跟真後端一致：第一次開啟時補一段「先前就聊過」的歷史，之後每則（含送出後的回播）
     * 都往後累積，重複開啟不會多出重複訊息。
     */
    const MAX_HISTORY = 200;
    const history = new Map();
    /** 每個帳號最後一次「已讀」的時間（mock 的 read 狀態；真的後端存在 DO 裡）。 */
    const readTs = new Map();
    const remember = (accountId, event) => {
        const list = history.get(accountId) ?? [];
        list.push(event);
        history.set(accountId, list.length > MAX_HISTORY ? list.slice(list.length - MAX_HISTORY) : list);
    };
    /** 第一次開啟某個帳號時塞一段假歷史（由舊到新；最後一則是自己說的）。 */
    const seedHistory = (account) => {
        if (history.has(account.id))
            return;
        const peers = PEERS[account.service];
        const now = Date.now();
        const events = [];
        if (account.room) {
            events.push({
                room: account.room,
                type: 'join',
                from: account.nickname,
                text: `進入 ${account.room}`,
                ts: now - 8 * 60_000,
            });
        }
        for (let index = 0; index < 4; index += 1) {
            const line = LINES[index % LINES.length];
            events.push({
                room: account.room,
                type: 'say',
                from: peers?.[index % (peers?.length || 1)] ?? '路人',
                text: line?.text ?? '大家安安',
                ts: now - (4 - index) * 60_000,
            });
        }
        events.push({
            room: account.room,
            type: 'say',
            from: account.nickname,
            text: '剛進來，大家好',
            ts: now - 30_000,
        });
        // 最後一則放「站方真實形狀」的樣本（JSON 歡迎詞／落單引號／中文引號），
        // 收件匣預覽與對話串都看得到同一個情況，截圖才驗得到顯示層的處理。
        const tail = TAIL_SAMPLES[accounts.findIndex((item) => item.id === account.id) % TAIL_SAMPLES.length];
        if (tail) {
            events.push({ room: account.room, type: tail.type, from: tail.from, text: tail.text, ts: now - 20_000 });
        }
        history.set(account.id, events);
    };
    const emit = (message) => {
        // 真的後端每一則事件都會進歷史（`GET /api/sessions/:id/events`）；mock 也照做，
        // 不然開房後拉歷史會是空的，跟左欄剛收到的訊息對不起來。
        remember(message.accountId, {
            room: message.room ?? null,
            type: message.kind,
            from: message.from,
            text: message.text,
            ...(message.to === undefined ? {} : { to: message.to }),
            ts: message.ts,
        });
        for (const handler of handlers)
            handler.onMessage(message);
    };
    const emitAccount = (account) => {
        for (const handler of handlers)
            handler.onAccount({ ...account });
    };
    const nextId = (prefix) => `${prefix}-${Date.now()}-${(seq += 1)}`;
    const later = (fn, ms) => {
        const timer = setTimeout(() => {
            timers.delete(timer);
            fn();
        }, ms);
        timers.add(timer);
        return timer;
    };
    const randomOf = (items) => items.length === 0 ? undefined : items[Math.floor(Math.random() * items.length)];
    const ambient = () => {
        const account = randomOf(accounts);
        const line = randomOf(LINES);
        if (!account || !line)
            return;
        const peer = randomOf(PEERS[account.service] ?? PEERS['ek21'] ?? []) ?? '路人';
        const isPm = Math.random() < 0.18;
        const kind = isPm ? 'pm' : 'say';
        emit({
            id: nextId('mock'),
            accountId: account.id,
            accountName: account.nickname,
            service: account.service,
            kind,
            from: peer,
            text: line.text,
            ...(isPm ? { to: account.nickname } : {}),
            ts: Date.now(),
            room: account.room,
        });
    };
    let ambientTimer;
    const startAmbient = () => {
        if (options.autoMessages === false || ambientTimer)
            return;
        const base = options.messageIntervalMs ?? 3200;
        ambientTimer = setInterval(() => {
            if (handlers.size === 0)
                return;
            // 抖動一下，避免訊息像節拍器。
            later(ambient, Math.random() * base * 0.8);
        }, base);
    };
    const stopAmbient = () => {
        if (ambientTimer)
            clearInterval(ambientTimer);
        ambientTimer = undefined;
    };
    const api = {
        async listAccounts() {
            return accounts.map((account) => ({ ...account }));
        },
        /**
         * `GET /api/sessions` 的完整版（帶 unread／lastActivityAt／lastMessagePreview）。
         *
         * 為什麼要有：demo 站若只實作 `listAccounts`，收件匣每列的預覽永遠是「—」，
         * 顯示層的預覽正規化（JSON 系統訊息／`img:` 標記）在畫面上根本驗不到。
         * 預覽取自同一份 seedHistory，所以跟點進去的對話串最後一則一致（= 真後端的行為）。
         */
        async listConversations() {
            return accounts.map((account, index) => {
                seedHistory(account);
                const events = history.get(account.id) ?? [];
                // 沒有 `from` 的 system 是後端紀錄，不算聊天內容（同 ThreadView 的規則）。
                const last = [...events].reverse().find((event) => !(event.type === 'system' && event.from.trim() === ''));
                const room = ROOMS.find((item) => item.id === account.room && item.service === account.service);
                const lastTs = last?.ts ?? 0;
                // 讀過就不再算未讀（markRead 會把時間寫進 readTs）。
                const unreadBase = index % 3 === 0 ? 2 : index % 3 === 1 ? 1 : 0;
                // 歸因欄位：照線上的樣子給（ek21 走 relay、UT 直連 socketio），
                // 免得 demo 整排「—」被當成後端沒修好。
                const accountName = MOCK_ACCOUNT_NAMES[index] ?? null;
                const loginMode = accountName === null ? 'guest' : 'account';
                const transport = account.service === 'ek21' ? 'relay' : 'socketio';
                return {
                    ...account,
                    loginMode,
                    account: accountName,
                    transport,
                    unread: lastTs > (readTs.get(account.id) ?? 0) ? unreadBase : 0,
                    lastActivityAt: last?.ts ?? null,
                    lastMessagePreview: last?.text ?? null,
                    roomOnline: room?.online ?? null,
                    lastReadTs: readTs.get(account.id) ?? 0,
                };
            });
        },
        /**
         * `GET /api/sessions/:id/events` 的 mock：回該帳號的歷史（第一次開啟先補一段假歷史）。
         *
         * 沒有這個方法，`useInbox` 開對話時會丟一條紅字（而且那句話含端點路徑），
         * demo／mock 模式一進來就掛著一條技術訊息；補上之後 mock 與真後端的行為一致。
         */
        async listEvents(accountId, options = {}) {
            const account = accounts.find((item) => item.id === accountId);
            if (!account)
                throw new Error(`帳號不存在：${accountId}`);
            seedHistory(account);
            const all = history.get(accountId) ?? [];
            const since = options.since ?? 0;
            const filtered = all.filter((event) => (event.ts ?? 0) > since);
            const limit = options.limit ?? 50;
            const events = filtered.slice(Math.max(0, filtered.length - limit));
            const last = events[events.length - 1];
            return {
                events: events.map((event) => ({ ...event })),
                order: 'asc',
                total: filtered.length,
                ...(last?.ts === undefined ? {} : { lastActivityAt: last.ts }),
            };
        },
        async createAccount(input) {
            // 假延遲：真的站別建一顆要 4–12 秒，畫面才有三階段可看（預設 0＝立刻成功）。
            if (typeof options.createDelayMs === 'number' && options.createDelayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, options.createDelayMs));
            }
            // 讓驗證能反覆按「重試」：把 options.createError 改回 null 就恢復正常。
            if (options.createError)
                throw new Error(options.createError);
            const usePool = input.loginMode === 'account' && (input.accountUsername ?? '') !== '';
            const stored = usePool
                ? pool.find((item) => item.username === input.accountUsername)
                : undefined;
            if (usePool && !stored) {
                throw new Error(`帳號池沒有 ${input.accountUsername}（先註冊或改用 guest）`);
            }
            const account = {
                id: `acct-${input.accountUsername ?? ''}-${Date.now()}`,
                service: input.service,
                nickname: input.nickname,
                room: input.room ?? null,
                online: true,
                createdAt: Date.now(),
                eventCount: 0,
            };
            if (stored)
                stored.lastLoginAt = Date.now();
            accounts.push(account);
            emitAccount(account);
            return { ...account };
        },
        async listAccountPool(service) {
            const rows = pool
                .filter((item) => service === undefined || item.service === service)
                .map((item) => ({ ...item }))
                .sort((a, b) => a.createdAt - b.createdAt);
            return { accounts: rows, count: rows.length };
        },
        async registerAccountPool(input) {
            const count = Math.max(1, Math.min(3, Math.trunc(input.count)));
            const since = Date.now() - lastRegisterAt;
            if (since < POOL_REGISTER_GAP_MS) {
                throw new Error(`註冊批次太密集，請 ${Math.ceil((POOL_REGISTER_GAP_MS - since) / 1000)} 秒後再試（mock）`);
            }
            lastRegisterAt = Date.now();
            const results = [];
            for (let index = 0; index < count; index += 1) {
                poolSeq += 1;
                const username = `mockpool${String(poolSeq).padStart(3, '0')}`;
                const ok = Math.random() > 0.2;
                pool.push({
                    service: input.service,
                    username,
                    createdAt: Date.now(),
                    lastLoginAt: ok ? Date.now() : null,
                    ok,
                    // 註冊成功就先算有憑證（真的後端也是登入成功才存 token，mock 用 ok 代表）。
                    hasToken: ok,
                    tokenSavedAt: ok ? Date.now() : null,
                    lastUsedAt: null,
                    usageCount: 0,
                    rebuilds: 0,
                    relogins: 0,
                    source: 'console',
                    held: false,
                });
                results.push(ok ? { username, ok } : { username, ok, reason: 'mock：登入失敗' });
            }
            return {
                service: input.service,
                requested: count,
                created: results.length,
                results,
                ...(input.room ? { room: input.room } : {}),
                hitSite: true,
            };
        },
        async renameAccountPool(service, username, displayName) {
            const item = pool.find((row) => row.service === service && row.username === username);
            if (!item)
                throw new Error(`帳號池沒有 ${username}（mock）`);
            const name = displayName === null ? '' : displayName.trim();
            if (displayName !== null) {
                if (name === '')
                    throw new Error('名字不能是空白（至少 1 個字）');
                if ([...name].length > 20)
                    throw new Error(`名字最多 20 字（現在 ${[...name].length} 字）`);
                const clash = pool.find((row) => row.service === service &&
                    row.username !== username &&
                    typeof row.displayName === 'string' &&
                    row.displayName.toLowerCase() === name.toLowerCase());
                if (clash)
                    throw new Error(`這個名字已經有人用了（${service} 的 ${clash.username}）`);
                item.displayName = name;
            }
            else {
                delete item.displayName;
            }
            return { ...item };
        },
        async deleteAccountPool(service, username) {
            const index = pool.findIndex((item) => item.service === service && item.username === username);
            if (index === -1)
                throw new Error(`帳號池沒有 ${username}`);
            pool.splice(index, 1);
        },
        async overview() {
            const byService = countsByService();
            const rejected = pool.filter((item) => item.ok === false).length;
            const dormant = accounts.filter((item) => item.dormant === true || item.online !== true).length;
            const last7d = registrationDays();
            const today = last7d[last7d.length - 1] ?? { date: '', ek21: null, ut: null, failed: null };
            const newestRun = runs[0];
            const allFailed = newestRun !== undefined && newestRun.created === 0 && newestRun.requested > 0;
            return {
                generatedAt: Date.now(),
                accounts: {
                    total: pool.length,
                    byService,
                    // 與 AccountsPage 的「可用」同一個定義（ok !== false && hasToken），不要另外發明一套。
                    usable: pool.filter((item) => item.ok !== false && item.hasToken === true).length,
                    rejected,
                    neverUsed: pool.filter((item) => item.lastLoginAt === null).length,
                },
                sessions: {
                    total: accounts.length,
                    online: accounts.filter((item) => item.online).length,
                    dormant,
                    // mock 沒有已讀模型，未讀一律 0（不要編一個跟收件匣對不上的數字）。
                    unreadTotal: 0,
                    byRoom: roomUsage(),
                },
                registration: {
                    lastBatchAt: newestRun?.at ?? null,
                    throttleRemainingMs: Math.max(0, lastRegisterAt + POOL_REGISTER_GAP_MS - Date.now()),
                    today: { ek21: today.ek21 ?? 0, ut: today.ut ?? 0, failed: today.failed ?? 0 },
                    last7d,
                },
                alerts: [
                    ...(lastRegisterAt + POOL_REGISTER_GAP_MS > Date.now()
                        ? [
                            {
                                level: 'info',
                                code: 'register_throttled',
                                message: `註冊冷卻中，還要 ${Math.ceil((lastRegisterAt + POOL_REGISTER_GAP_MS - Date.now()) / 1000)} 秒`,
                                at: lastRegisterAt,
                            },
                        ]
                        : []),
                    ...(allFailed
                        ? [
                            {
                                level: 'warn',
                                code: 'last_run_all_failed',
                                message: `最近一批 ${newestRun.service.toUpperCase()} 全滅（0/${newestRun.requested}）`,
                                at: newestRun.at,
                            },
                        ]
                        : []),
                    ...(rejected > 0
                        ? [
                            {
                                level: 'danger',
                                code: 'accounts_rejected',
                                message: `${rejected} 顆帳號被站方拒絕`,
                                at: Date.now(),
                            },
                        ]
                        : []),
                ],
            };
        },
        async listRuns(query) {
            const limit = Math.min(Math.max(Math.trunc(query?.limit ?? 20), 1), 200);
            const rows = query?.service ? runs.filter((run) => run.service === query.service) : runs;
            // 深拷貝：呼叫端改陣列不該反向污染 mock 的歷史資料。
            return {
                runs: rows.slice(0, limit).map((run) => ({
                    ...run,
                    errors: [...run.errors],
                    poolAfter: { ...run.poolAfter },
                })),
                nextCursor: null,
            };
        },
        async system() {
            const checkedAt = Date.now();
            const newestRun = runs[0];
            return {
                checkedAt,
                registration: {
                    lastBatchAt: newestRun?.at ?? null,
                    throttleRemainingMs: Math.max(0, lastRegisterAt + POOL_REGISTER_GAP_MS - Date.now()),
                },
                relay: {
                    configured: true,
                    reachable: true,
                    latencyMs: 412,
                    forbiddenBackoffSec: 0,
                    checkedAt,
                },
                sites: enabledServices().map((service) => ({
                    service,
                    reachable: true,
                    latencyMs: service === 'ek21' ? 233 : 190,
                    checkedAt,
                })),
                scheduler: {
                    lastRunAt: Date.now() - 6 * 3_600_000,
                    lastResult: newestRun !== undefined && newestRun.created === 0 ? 'failed' : 'ok',
                    source: 'cron',
                },
            };
        },
        /**
         * `POST /api/sessions/:id/read` 的 mock：真的把未讀清掉。
         * 沒有這個方法時 `useInbox` 會在畫面上留一條「這個 ApiClient 沒有 markRead」的紅字，
         * demo／截圖會多一個跟主題無關的錯誤條。
         */
        async markRead(accountId, ts) {
            const lastReadTs = ts ?? Date.now();
            const index = accounts.findIndex((item) => item.id === accountId);
            if (index === -1)
                throw new Error(`帳號不存在：${accountId}`);
            readTs.set(accountId, lastReadTs);
            return { ok: true, lastReadTs, lastReadAt: lastReadTs, unread: 0 };
        },
        async listRooms(service) {
            return ROOMS.filter((room) => room.service === service).map((room) => ({ ...room }));
        },
        async joinRoom(accountId, room) {
            const account = accounts.find((item) => item.id === accountId);
            if (!account)
                throw new Error(`帳號不存在：${accountId}`);
            account.room = room;
            emitAccount(account);
            later(() => emit({
                id: nextId('mock'),
                accountId: account.id,
                accountName: account.nickname,
                service: account.service,
                kind: 'system',
                from: '',
                text: `已進入房間 ${room}`,
                ts: Date.now(),
                room,
            }), 120);
        },
        async say(accountId, text) {
            const account = accounts.find((item) => item.id === accountId);
            if (!account)
                throw new Error(`帳號不存在：${accountId}`);
            if (options.sayError)
                throw new Error(options.sayError);
            // 站方會不會把自己這句話回播給自己：ek21 會、UT 不會（mock 預設照真實站別走）。
            if (options.echoSay ?? expectsEcho(account.service, 'say')) {
                later(() => emit({
                    id: nextId('mock'),
                    accountId: account.id,
                    accountName: account.nickname,
                    service: account.service,
                    kind: 'say',
                    from: account.nickname,
                    text,
                    ts: Date.now(),
                    room: account.room,
                }), 250);
            }
        },
        async pm(accountId, target, text) {
            const account = accounts.find((item) => item.id === accountId);
            if (!account)
                throw new Error(`帳號不存在：${accountId}`);
            if (options.sayError)
                throw new Error(options.sayError);
            if (options.echoSay ?? expectsEcho(account.service, 'pm')) {
                later(() => emit({
                    id: nextId('mock'),
                    accountId: account.id,
                    accountName: account.nickname,
                    service: account.service,
                    kind: 'pm',
                    from: account.nickname,
                    text,
                    to: target,
                    ts: Date.now(),
                    room: account.room,
                }), 250);
            }
        },
        async close(accountId) {
            const index = accounts.findIndex((item) => item.id === accountId);
            if (index === -1)
                throw new Error(`帳號不存在：${accountId}`);
            const [removed] = accounts.splice(index, 1);
            if (removed)
                emitAccount({ ...removed, online: false });
        },
        subscribe(next) {
            handlers.add(next);
            startAmbient();
            for (const account of accounts)
                next.onAccount({ ...account });
            return () => {
                handlers.delete(next);
                if (handlers.size === 0)
                    stopAmbient();
                for (const timer of timers)
                    clearTimeout(timer);
                timers.clear();
            };
        },
    };
    return {
        ...api,
        /**
         * `GET /api/sessions` 的原始列：`listConversations` 的 mock 列本來就帶
         * `loginMode`／`transport`／`account`，直接回同一份，不另外造一組假資料，
         * 兩條路徑不可能給出不同數字。
         */
        async listSessionRows() {
            const rows = (await api.listConversations?.()) ?? [];
            // `Conversation` 是投影後的介面；mock 的列本來就帶歸因欄位，這裡只換成寬鬆形狀。
            return rows;
        },
    };
}
//# sourceMappingURL=mock-client.js.map